<template>
  <thread-item-wrapper :comments="log.data.comments">
    <template #content>
      <div class="flex flex-col space-y-2 border-b text-gray-500">
        <div class="task-meta-wrapper px-4 py-2">
          <div class="wrap flex justify-between">
            <div class="meta flex items-center space-x-2">
              <lf-user-selection
                v-tooltip="{
                  content: $t('ACTIVITY.TASK.ASSIGNEE', {
                    name: formatUserFullName(log.data.assignee),
                  }),
                  onShow: () => log.data.assignee,
                }"
                name="assignee"
                :value="log.data?.assignee"
                :initial-user-selection="log.data?.assignee"
                :get-paginated-options="getUsersByRole"
                :disabled="!isEditing"
                avatar-only-trigger-button
                value-type="user"
                data-cy="assignee"
                @change="updateAssignee"
              />
              <task-priority-dropdown
                :priority-level="log.data.priority_level"
                :disabled="!isEditing"
                data-cy="priority"
                @change="updatePriority"
              />

              <task-due-date
                :date="log.data.due_at"
                :disabled="!isEditing"
                @update="updateDueDate"
                @click.stop
              />
              <business-name
                :id="log.id"
                class="max-w-fit"
                :name="log.business_legal_name"
                :application-id="log.application_id"
                :type="log.type"
                @click.stop
              />
            </div>
            <div class="actions flex items-center space-x-2">
              <task-status-dropdown
                :task-status="log.data.status"
                :is-loading="isUpdating"
                data-cy="task-status"
                @change="statusChange"
              />
              <loader :is-loading="deleteLoading" />
              <action-menu-wrapper
                v-if="!isEditing && !shouldReloadMenu"
                :items="menuOptions"
                :offset="offset"
                teleport
                data-cy="action-menu"
              />
            </div>
          </div>
        </div>
        <task-description
          :description="log.data.body"
          :disabled="!isEditing"
          data-cy="description"
          @update="description = $event"
          @submit="updateDescription"
        />
      </div>
    </template>
    <template #footer>
      <div v-if="log" class="flex w-full justify-between space-y-1 bg-white">
        <div class="flex-row-full-justify-between wrapper">
          <div class="wrap-children flex items-center space-x-2">
            <span class="text-xs font-medium">
              {{ $t("ACTIVITY.TASK.FROM") }}
            </span>
            <avatar-with-name :user="log.data.user" data-cy="user-name" />
            <span v-if="log.data?.created_at">&#8226;</span>
            <date-time :date="log.data?.created_at" />
          </div>
        </div>
      </div>
    </template>
  </thread-item-wrapper>
</template>
<script lang="ts" setup>
import { computed, nextTick, provide, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import isEmpty from "lodash/isEmpty";
import { storeToRefs } from "pinia";

import { LogEndpointType, type TaskStatuses } from "@/enums/communicationLogs";
import { formatUserFullName } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { usePromiseWrapper } from "@/hooks/common";
import {
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from "@/hooks/queries/useInbox";
import usersApiService from "@/services/modules/users";
import { useCommunicationStore } from "@/stores/communication";

import ThreadItemWrapper from "@/components/communicationLogs/communication/threads/openState/ThreadItemWrapper.vue";
import TaskDescription from "@/components/communicationLogs/forms/TaskDescription.vue";
import TaskDueDate from "@/components/communicationLogs/forms/TaskDueDate.vue";
import TaskPriorityDropdown from "@/components/communicationLogs/forms/TaskPriorityDropdown.vue";
import TaskStatusDropdown from "@/components/communicationLogs/forms/TaskStatusDropdown.vue";
import BusinessName from "@/components/inbox/BusinessName.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";
import AvatarWithName from "@/components/ui/AvatarWithName.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import LfUserSelection from "@/components/ui/inputs/LfUserSelection.vue";

import type { TaskPriorityLevels } from "@/enums/communicationLogs";
import type { TaskLog } from "@/models/communicationLogs";

const emit = defineEmits<{
  close: [];
}>();

const { log } = defineProps<{
  log: TaskLog;
}>();

const offset = { left: -58, top: 43 };

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Task,
  logId: log.data.id,
});

const { t } = useI18n();
const auth = useAuth();
const { authClientSettings: client } = useClients();
const { logs } = storeToRefs(useCommunicationStore());

const isEditing = ref(true);
const shouldReloadMenu = ref(false);
const description = ref(log.data.body);

const { mutateAsync: updateTaskMutation, isPending: isUpdating } =
  useUpdateTaskMutation();
const { mutateAsync: deleteTaskMutation } = useDeleteTaskMutation();

const updatePriority = async (priority: TaskPriorityLevels) => {
  if (!log.data.id) {
    return;
  }
  await updateTaskMutation({
    id: log.data.id,
    priority_level: priority,
  });
};

const statusChange = async (status: TaskStatuses) => {
  if (log.data.id) {
    await updateTaskMutation({ id: log.data.id, status });
    return;
  }
};

const updateDueDate = async (due_at: string) => {
  if (!log.data.id) {
    return;
  }
  await updateTaskMutation({
    id: log.data.id,
    due_at: new Date(due_at).toISOString(),
  });
};

const updateAssignee = async (assignee_id: number) => {
  if (!log.data.id) {
    return;
  }
  await updateTaskMutation({
    id: log.data.id,
    assignee_id,
  });
};

const updateDescription = async () => {
  if (!log.data.id || description.value === log.data.body) {
    return;
  }
  await updateTaskMutation({
    id: log.data.id,
    description: description.value,
  });
};

const { loading: deleteLoading, fetchWrapper: deleteTask } = usePromiseWrapper(
  async () => {
    emit("close");
    await deleteTaskMutation(log.data.id);
  }
);

const menuOptions = computed(() => {
  const options: MenuItem[] = [];

  if (auth.isAdmin) {
    options.push({
      label: t("COMMON.DELETE"),
      handler: deleteTask,
      class: "text-red-500",
    });
  }
  return options;
});

const getUsersByRole = (params: Record<string, string | number> = {}) => {
  const isAClient = !isEmpty(client.value);
  if (isAClient) {
    return usersApiService.getClientUsers(client.value.id, params);
  }
  return usersApiService.getEmployees(params);
};

watch(logs, (updatedLogs, oldLogs) => {
  if (updatedLogs.length < oldLogs.length) {
    shouldReloadMenu.value = true;
    nextTick(() => {
      shouldReloadMenu.value = false;
    });
  }
});

defineExpose({ deleteTask });
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.task-meta-wrapper {
  :deep() {
    .meta {
      @apply flex-wrap gap-y-2;
    }
    .actions {
      @apply h-10 min-w-[180px] items-center justify-end;
    }
  }
}
</style>
